/*
 * apiFetch — thin wrapper around `fetch` for /api/* calls.
 *
 * Adds two things every authenticated API call now needs after SEC-06:
 * 1. `credentials: 'include'` so the HttpOnly admin_session /
 *    customer_session cookie travels with the request. The CORS
 *    config in server/src/index.js sends `Access-Control-Allow-
 *    Credentials: true`, so this works cross-origin too.
 * 2. `X-CSRF-Token` header (read from the csrf_token cookie that the
 *    server plants at login). Required on all non-GET/HEAD/OPTIONS
 *    requests — the server will enforce this in Phase 4. Until then,
 *    sending the header is harmless: nothing checks it yet.
 *
 * Phase 3 of the SEC-06 migration: replace every direct `fetch(`
 * call to /api/* with `apiFetch(`. Existing Authorization headers
 * (constructed from localStorage in the legacy code) can stay during
 * the dual-write window — they're harmless when the cookie path
 * also works. Phase 5 removes them.
 *
 * Public endpoints (POST /api/quotes, /api/contacts, /api/newsletter,
 * etc.) can use apiFetch too — the credentials flag does nothing on
 * unauthenticated requests, and the CSRF header is also harmless on
 * endpoints that don't validate it. reCAPTCHA stays the bot defence
 * on those routes; the CSRF protection layered on top is bonus.
 */

// Parse the csrf_token cookie out of document.cookie. Returns '' if
// not present (e.g. the user has never logged in).
function getCsrfToken() {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function apiFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = { ...(options.headers || {}) };

  if (WRITE_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  return fetch(url, {
    ...options,
    method,
    credentials: 'include',
    headers,
  });
}

export { getCsrfToken };
